import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MyReferrals = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('stats');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, historyRes, tiersRes, leaderboardRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/referrals/stats`, { headers }),
        axios.get(`${BACKEND_URL}/api/referrals/history`, { headers }),
        axios.get(`${BACKEND_URL}/api/referrals/tiers`),
        axios.get(`${BACKEND_URL}/api/referrals/leaderboard`)
      ]);

      setStats(statsRes.data);
      setHistory(historyRes.data.referrals || []);
      setTiers(tiersRes.data.tiers || []);
      setLeaderboard(leaderboardRes.data.leaderboard || []);
    } catch (error) {
      console.error('Error loading referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    if (stats?.referral_code) {
      navigator.clipboard.writeText(stats.referral_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareCode = () => {
    const text = `¡Únete a GPT-JDP! Usa mi código ${stats?.referral_code} y obtén 500 créditos gratis. https://gpt-jdp.com/register?ref=${stats?.referral_code}`;
    if (navigator.share) {
      navigator.share({ text });
    } else {
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getTierColor = (tierId) => {
    const colors = {
      bronze: 'from-amber-600 to-amber-800',
      silver: 'from-gray-400 to-gray-600',
      gold: 'from-yellow-400 to-yellow-600',
      platinum: 'from-purple-400 to-purple-600'
    };
    return colors[tierId] || colors.bronze;
  };

  const getTierIcon = (tierId) => {
    const icons = {
      bronze: '🥉',
      silver: '🥈',
      gold: '🥇',
      platinum: '💎'
    };
    return icons[tierId] || '🥉';
  };

  if (loading) {
    return (
      <div className="font-display bg-background-light dark:bg-background-dark min-h-screen">
        <div className="px-4 sm:px-10 md:px-20 lg:px-40">
          <Header />
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="font-display bg-background-light dark:bg-background-dark min-h-screen">
      <div className="px-4 sm:px-10 md:px-20 lg:px-40">
        <Header />
        
        <div className="py-8 max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-black text-gray-900 dark:text-white mb-2">
              🎁 Programa de Referidos
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Invita amigos y gana créditos por cada registro
            </p>
          </div>

          {/* Referral Code Card */}
          {stats && (
            <div className="bg-gradient-to-r from-primary to-blue-600 rounded-2xl p-6 mb-8 text-white">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div>
                  <p className="text-sm opacity-80 mb-1">Tu código de referido</p>
                  <p className="text-3xl font-black tracking-wider">{stats.referral_code}</p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={copyCode}
                    className="px-6 py-3 bg-white/20 hover:bg-white/30 rounded-lg font-semibold transition-colors flex items-center gap-2"
                  >
                    {copied ? '✓ Copiado' : '📋 Copiar'}
                  </button>
                  <button
                    onClick={shareCode}
                    className="px-6 py-3 bg-white text-primary hover:bg-gray-100 rounded-lg font-semibold transition-colors flex items-center gap-2"
                  >
                    📤 Compartir
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white dark:bg-[#1a2530] rounded-xl p-4 shadow-lg">
                <p className="text-sm text-gray-500 dark:text-gray-400">Referidos Exitosos</p>
                <p className="text-3xl font-black text-gray-900 dark:text-white">{stats.successful_referrals}</p>
              </div>
              <div className="bg-white dark:bg-[#1a2530] rounded-xl p-4 shadow-lg">
                <p className="text-sm text-gray-500 dark:text-gray-400">Pendientes</p>
                <p className="text-3xl font-black text-yellow-600">{stats.pending_referrals}</p>
              </div>
              <div className="bg-white dark:bg-[#1a2530] rounded-xl p-4 shadow-lg">
                <p className="text-sm text-gray-500 dark:text-gray-400">Créditos Ganados</p>
                <p className="text-3xl font-black text-green-600">{stats.total_credits_earned.toLocaleString()}</p>
              </div>
              <div className="bg-white dark:bg-[#1a2530] rounded-xl p-4 shadow-lg">
                <p className="text-sm text-gray-500 dark:text-gray-400">Tu Nivel</p>
                <p className="text-2xl font-black">
                  {getTierIcon(stats.current_tier)} {stats.tier_benefits?.name}
                </p>
              </div>
            </div>
          )}

          {/* Tier Progress */}
          {stats && stats.next_tier && (
            <div className="bg-white dark:bg-[#1a2530] rounded-xl p-6 mb-8 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Progreso al siguiente nivel</p>
                  <p className="font-bold text-gray-900 dark:text-white">
                    {stats.referrals_to_next_tier} referidos más para {stats.next_tier}
                  </p>
                </div>
                <span className="text-3xl">{getTierIcon(stats.next_tier)}</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                <div 
                  className={`bg-gradient-to-r ${getTierColor(stats.current_tier)} h-3 rounded-full transition-all duration-500`}
                  style={{ 
                    width: `${Math.min(100, (stats.successful_referrals / (stats.successful_referrals + stats.referrals_to_next_tier)) * 100)}%` 
                  }}
                />
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto">
            {[
              { id: 'stats', label: '📊 Niveles', icon: '' },
              { id: 'history', label: '📜 Historial', icon: '' },
              { id: 'leaderboard', label: '🏆 Ranking', icon: '' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-lg font-semibold whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-primary text-white'
                    : 'bg-white dark:bg-[#1a2530] text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#283039]'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="bg-white dark:bg-[#1a2530] rounded-xl shadow-lg overflow-hidden">
            {activeTab === 'stats' && (
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Niveles de Referido</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {tiers.map((tier) => (
                    <div 
                      key={tier.id}
                      className={`rounded-xl p-4 border-2 ${
                        stats?.current_tier === tier.id 
                          ? 'border-primary bg-primary/5' 
                          : 'border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-3xl">{getTierIcon(tier.id)}</span>
                        <div>
                          <p className="font-bold text-gray-900 dark:text-white">{tier.name}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {tier.min_referrals}+ referidos
                          </p>
                        </div>
                        {stats?.current_tier === tier.id && (
                          <span className="ml-auto px-2 py-1 bg-primary text-white text-xs rounded-full">
                            ACTUAL
                          </span>
                        )}
                      </div>
                      
                      {/* Credits Section */}
                      <div className="space-y-2 text-sm mb-3">
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">Por cada referido</span>
                          <span className="font-semibold text-green-600">+{tier.credits_per_referral} créditos</span>
                        </div>
                        {tier.first_referral_bonus > 0 && (
                          <div className="flex justify-between bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded-lg">
                            <span className="text-yellow-700 dark:text-yellow-400">🎁 Primer referido</span>
                            <span className="font-semibold text-yellow-600">+{tier.first_referral_bonus} extra</span>
                          </div>
                        )}
                        {tier.bonus_credits > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-500 dark:text-gray-400">Bonus al alcanzar</span>
                            <span className="font-semibold text-purple-600">+{tier.bonus_credits.toLocaleString()}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Perks Section */}
                      {tier.perks && tier.perks.length > 0 && (
                        <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">BENEFICIOS EXCLUSIVOS:</p>
                          <div className="space-y-1">
                            {tier.perks.map((perk, idx) => (
                              <p key={idx} className="text-xs text-primary">
                                {perk.description}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Special Benefit */}
                      {!tier.perks?.length && (
                        <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                          <span className="text-primary font-medium text-xs">{tier.special_benefit}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'history' && (
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Historial de Referidos</h3>
                {history.length === 0 ? (
                  <div className="text-center py-8">
                    <span className="text-5xl mb-4 block">👥</span>
                    <p className="text-gray-500 dark:text-gray-400">
                      Aún no tienes referidos. ¡Comparte tu código!
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {history.map((ref, index) => (
                      <div 
                        key={ref.id || index}
                        className="flex items-center justify-between p-4 bg-gray-50 dark:bg-[#283039] rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                            <span className="text-primary font-bold">
                              {ref.referred_name?.charAt(0) || 'U'}
                            </span>
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900 dark:text-white">
                              {ref.referred_name || 'Usuario'}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {new Date(ref.created_at).toLocaleDateString('es-CL')}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-semibold ${
                            ref.status === 'credited' ? 'text-green-600' : 'text-yellow-600'
                          }`}>
                            {ref.status === 'credited' ? `+${ref.credits_awarded_referrer}` : 'Pendiente'}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {ref.status === 'credited' ? 'créditos' : 'en proceso'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'leaderboard' && (
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Top Referidores</h3>
                {leaderboard.length === 0 ? (
                  <div className="text-center py-8">
                    <span className="text-5xl mb-4 block">🏆</span>
                    <p className="text-gray-500 dark:text-gray-400">
                      ¡Sé el primero en el ranking!
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {leaderboard.map((entry, index) => (
                      <div 
                        key={index}
                        className={`flex items-center justify-between p-4 rounded-lg ${
                          index < 3 
                            ? 'bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20' 
                            : 'bg-gray-50 dark:bg-[#283039]'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">
                            {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${entry.rank}`}
                          </span>
                          <div>
                            <p className="font-semibold text-gray-900 dark:text-white">{entry.name}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{entry.tier}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900 dark:text-white">{entry.referrals}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">referidos</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* How it works */}
          <div className="mt-8 bg-white dark:bg-[#1a2530] rounded-xl p-6 shadow-lg">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">¿Cómo funciona?</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <span className="text-4xl block mb-2">📤</span>
                <p className="font-semibold text-gray-900 dark:text-white mb-1">1. Comparte tu código</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Envía tu código único a amigos y familiares
                </p>
              </div>
              <div className="text-center">
                <span className="text-4xl block mb-2">👤</span>
                <p className="font-semibold text-gray-900 dark:text-white mb-1">2. Se registran</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Tus referidos se registran con tu código
                </p>
              </div>
              <div className="text-center">
                <span className="text-4xl block mb-2">🎁</span>
                <p className="font-semibold text-gray-900 dark:text-white mb-1">3. ¡Ganan ambos!</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Tú recibes créditos y ellos también
                </p>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
};

export default MyReferrals;
